import { createFileRoute, Link } from '@tanstack/react-router';
import { useState } from 'react';
import { BILLING_COMPANY_ID } from '@accounting/shared';
import { useActiveCompanyId } from '../../stores/useCompanyStore';
import { useAssembleEomPacket, useEomReadiness } from '../../hooks/useEomFinancials';
import { formatAccountingDate } from '../../lib/formatters';

export const Route = createFileRoute('/_auth/eom-financials')({
  component: EomFinancialsPage,
});

function errorText(err: unknown): string {
  const body = (err as { body?: { error?: string } })?.body;
  if (body?.error) return body.error;
  return (err as Error)?.message ?? 'Something went wrong.';
}

/** Default to LAST month — the packet is assembled after a month closes. */
function lastMonth(): string {
  const now = new Date();
  const d = new Date(Date.UTC(now.getUTCFullYear(), now.getUTCMonth() - 1, 1));
  return d.toISOString().slice(0, 7);
}

function EomFinancialsPage() {
  const companyId = useActiveCompanyId();
  const [month, setMonth] = useState(lastMonth());
  const { data: readiness, isLoading, error } = useEomReadiness(companyId, month);
  const assemble = useAssembleEomPacket(companyId);

  if (companyId !== BILLING_COMPANY_ID) {
    return (
      <div className="card" style={{ textAlign: 'center', padding: 32 }}>
        <h1 style={{ marginTop: 0 }}>EOM Financials</h1>
        <p className="muted">
          The month-end regulatory packet is specific to Bateman Capital Management. Switch to
          that company to assemble one.
        </p>
      </div>
    );
  }

  return (
    <div>
      <div style={{ marginBottom: 16 }}>
        <h1 style={{ fontSize: 24, margin: 0 }}>EOM Financials</h1>
        <p className="muted" style={{ margin: '4px 0 0', fontSize: 13 }}>
          The monthly packet kept on file: balance sheet and P&amp;L year to date, every
          reconciliation proof, and the statements behind them — as one PDF.
        </p>
      </div>

      <div className="card" style={{ marginBottom: 16 }}>
        <label style={{ fontSize: 13 }}>
          Month{' '}
          <input
            type="month"
            value={month}
            onChange={(e) => {
              setMonth(e.target.value);
              assemble.reset();
            }}
          />
        </label>
        {readiness && (
          <p className="muted" style={{ fontSize: 12, margin: '8px 0 0' }}>
            Balance sheet as of {formatAccountingDate(readiness.periodTo)} · P&amp;L{' '}
            {formatAccountingDate(readiness.fiscalYearStart)} to{' '}
            {formatAccountingDate(readiness.periodTo)} · saved as{' '}
            <strong>{month.replace('-', '')}.pdf</strong> under {`"EOM Financials"`}
          </p>
        )}
      </div>

      {isLoading && <p className="muted">Checking {month}…</p>}
      {error != null && (
        <p className="tag bad" style={{ display: 'block', padding: 8 }}>{errorText(error)}</p>
      )}

      {readiness && (
        <div className="card">
          <h2 style={{ fontSize: 16, margin: '0 0 4px' }}>Reconciliation status</h2>
          <p className="muted" style={{ margin: '0 0 12px', fontSize: 13 }}>
            Every reconcilable account needs a completed reconciliation dated inside {month}.
          </p>

          <table style={{ width: '100%', fontSize: 13 }}>
            <thead>
              <tr>
                <th style={{ textAlign: 'left' }}>Account</th>
                <th style={{ textAlign: 'left' }}>GL</th>
                <th style={{ textAlign: 'left' }}>Reconciled</th>
              </tr>
            </thead>
            <tbody>
              {readiness.accounts.map((a) => (
                <tr key={a.bankAccountId}>
                  <td>{a.accountName}</td>
                  <td className="muted">
                    {a.glCode ? `${a.glCode} ${a.glName ?? ''}` : <em>unmapped</em>}
                  </td>
                  <td>
                    {a.reconciled ? (
                      <span className="tag ok">
                        {a.statementDates.map((d) => formatAccountingDate(d)).join(', ')}
                      </span>
                    ) : (
                      <span className="tag bad">not reconciled</span>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>

          {!readiness.ready && (
            <p className="tag bad" style={{ display: 'block', padding: 8, marginTop: 12 }}>
              {month} is not fully reconciled, so the packet cannot be assembled.
              {readiness.missing.length > 0 && (
                <> Still outstanding: <strong>{readiness.missing.join(', ')}</strong>.</>
              )}{' '}
              <Link to="/reconcile">Go and reconcile</Link>, then come back.
            </p>
          )}

          {readiness.ready && readiness.statementCount === 0 && (
            <p className="tag warn" style={{ display: 'block', padding: 8, marginTop: 12 }}>
              Everything is reconciled, but there are no statement PDFs on file for {month}. The
              packet will contain the reports and the reconciliation proofs without the
              statements behind them.
            </p>
          )}

          {readiness.existingDocumentId && (
            <p className="tag warn" style={{ display: 'block', padding: 8, marginTop: 12 }}>
              A packet for {month} already exists. Assembling again replaces it with a freshly
              built one.
            </p>
          )}

          {assemble.error != null && (
            <p className="tag bad" style={{ display: 'block', padding: 8, marginTop: 12 }}>
              {errorText(assemble.error)}
            </p>
          )}

          {assemble.data && (
            <div className="tag ok" style={{ display: 'block', padding: 8, marginTop: 12 }}>
              Filed <strong>{assemble.data.fileName}</strong> under {assemble.data.folder}, dated{' '}
              {formatAccountingDate(assemble.data.documentDate)} ·{' '}
              {Math.round(assemble.data.sizeBytes / 1024).toLocaleString()} KB
              <ul style={{ margin: '6px 0 0', paddingLeft: 20 }}>
                {assemble.data.included.map((s) => (
                  <li key={s}>{s}</li>
                ))}
              </ul>
              {assemble.data.skipped.length > 0 && (
                <p style={{ margin: '6px 0 0' }}>
                  Left out: {assemble.data.skipped.map((s) => `${s.name} (${s.reason})`).join('; ')}
                </p>
              )}
              <p style={{ margin: '6px 0 0' }}>
                <Link to="/documents">Open Documents</Link>
              </p>
            </div>
          )}

          <div style={{ marginTop: 12, display: 'flex', gap: 12, alignItems: 'center' }}>
            <button
              type="button"
              className="btn-primary"
              disabled={!readiness.ready || assemble.isPending}
              onClick={() => assemble.mutate(month)}
            >
              {assemble.isPending ? 'Assembling…' : `Assemble ${month.replace('-', '')}.pdf`}
            </button>
            <span className="muted" style={{ fontSize: 12 }}>
              {readiness.statementCount} statement{readiness.statementCount === 1 ? '' : 's'} on
              file for {month}
            </span>
          </div>
        </div>
      )}
    </div>
  );
}
